import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Layout, RefreshCw, CheckCircle, Clock, RotateCcw, Trash2,
  MessageSquare, MessageCircle, Zap, ChevronRight, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { id: 'pending_review', label: 'Pending Review',      color: '#f0c27f', bg: 'rgba(240,194,127,0.06)' },
  { id: 'scheduled',      label: 'Approved',            color: '#7c5efa', bg: 'rgba(124,94,250,0.06)'  },
  { id: 'published',      label: 'Published',           color: '#3dffa0', bg: 'rgba(61,255,160,0.06)'  },
  { id: 'failed',         label: 'Declined / Revising', color: '#eb5757', bg: 'rgba(235,87,87,0.06)'   },
];

const PLATFORM_COLORS = {
  linkedin: '#0a66c2', twitter: '#1da1f2', facebook: '#1877f2',
  instagram: '#e4405f', blog: '#ff6b35',
};

const EVENT_META = {
  notification_sent: { label: 'Draft sent for review', color: '#f0c27f', icon: '📤' },
  approved:          { label: 'Approved',               color: '#3dffa0', icon: '✅' },
  declined:          { label: 'Declined + rewriting',   color: '#eb5757', icon: '🔄' },
  rewritten:         { label: 'Rewrite ready',          color: '#7c5efa', icon: '✍️' },
  published:         { label: 'Published',              color: '#00d4b8', icon: '🚀' },
};

function timeSince(ts) {
  const seconds = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (seconds < 60)  return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds/60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds/3600)}h ago`;
  return `${Math.floor(seconds/86400)}d ago`;
}

function DiscordFeedPanel({ open, onToggle }) {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchFeed = useCallback(async () => {
    try {
      const [feedRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/discord/feed?limit=30`),
        axios.get(`${API}/api/discord/stats`),
      ]);
      setEvents(feedRes.data.events || []);
      setStats(statsRes.data.stats || {});
    } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchFeed().finally(() => setLoading(false));
    const interval = setInterval(fetchFeed, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [open, fetchFeed]);

  if (!open) {
    return (
      <button onClick={onToggle} data-testid="discord-feed-toggle"
        className="flex flex-col items-center gap-1 py-3 px-2 rounded-xl transition-all hover:opacity-80"
        title="Discord Activity Feed"
        style={{ background: 'rgba(88,101,242,0.1)', border: '1px solid rgba(88,101,242,0.25)', color: '#7289da', writingMode: 'vertical-rl' }}>
        <MessageCircle size={14} />
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ fontSize: 9 }}>Discord Feed</span>
      </button>
    );
  }

  return (
    <div className="flex flex-col flex-shrink-0 h-full overflow-hidden"
      style={{ width: 300, borderLeft: '1px solid rgba(88,101,242,0.2)', background: 'rgba(8,8,16,0.98)' }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(88,101,242,0.15)', background: 'rgba(88,101,242,0.06)' }}>
        <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(88,101,242,0.2)' }}>
          <MessageCircle size={13} style={{ color: '#7289da' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-black text-white">Discord Activity</div>
          <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {loading ? 'Loading...' : `${events.length} events · auto-refresh 30s`}
          </div>
        </div>
        <button onClick={onToggle} className="p-1 rounded-lg hover:opacity-70 flex-shrink-0"
          style={{ color: 'rgba(255,255,255,0.3)' }} title="Collapse">
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Stats row */}
      {Object.keys(stats).length > 0 && (
        <div className="flex gap-1.5 px-3 py-2 flex-shrink-0 flex-wrap"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          {Object.entries(stats).map(([type, count]) => {
            const m = EVENT_META[type] || { color: '#666', label: type };
            return (
              <div key={type} className="flex items-center gap-1 px-1.5 py-0.5 rounded"
                style={{ background: `${m.color}12`, border: `1px solid ${m.color}30` }}>
                <span className="text-[9px] font-bold" style={{ color: m.color }}>{count}</span>
                <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  {type.replace(/_/g, ' ')}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Event feed */}
      <div className="flex-1 overflow-y-auto" data-testid="discord-feed-list">
        {loading && events.length === 0 ? (
          <div className="flex items-center justify-center h-20">
            <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: '#7289da', borderTopColor: 'transparent' }} />
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center px-4">
            <MessageCircle size={24} style={{ color: 'rgba(114,137,218,0.2)', marginBottom: 8 }} />
            <div className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.2)' }}>No activity yet</div>
            <div className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.12)' }}>
              Actions appear here as content moves through the pipeline
            </div>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {events.map(evt => {
              const m = EVENT_META[evt.event_type] || { color: '#7289da', label: evt.event_type, icon: '•' };
              return (
                <div key={evt.id} data-testid={`discord-event-${evt.id}`}
                  className="rounded-xl p-2.5 transition-all"
                  style={{ background: `${m.color}0a`, border: `1px solid ${m.color}18` }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[11px]">{m.icon}</span>
                    <span className="text-[10px] font-bold flex-1 truncate" style={{ color: m.color }}>
                      {m.label}
                    </span>
                    <span className="text-[9px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.2)' }}>
                      {timeSince(evt.timestamp)}
                    </span>
                  </div>

                  <div className="text-[11px] font-semibold text-white leading-tight truncate mb-0.5">
                    {evt.title || 'Untitled'}
                  </div>

                  <div className="flex items-center gap-1.5">
                    {evt.platform && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded capitalize"
                        style={{
                          background: `${PLATFORM_COLORS[evt.platform] || '#666'}18`,
                          color: PLATFORM_COLORS[evt.platform] || '#999',
                        }}>
                        {evt.platform}
                      </span>
                    )}
                    {evt.version > 1 && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(240,194,127,0.15)', color: '#f0c27f' }}>
                        V{evt.version}
                      </span>
                    )}
                    {evt.discord_delivered && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded ml-auto"
                        style={{ background: 'rgba(88,101,242,0.15)', color: '#7289da' }}>
                        Discord
                      </span>
                    )}
                  </div>

                  {evt.feedback && (
                    <div className="mt-1.5 text-[10px] leading-relaxed italic px-2 py-1 rounded"
                      style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)' }}>
                      "{evt.feedback.slice(0, 80)}{evt.feedback.length > 80 ? '...' : ''}"
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Live indicator */}
      <div className="flex items-center gap-1.5 px-3 py-2 flex-shrink-0"
        style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#3dffa0' }} />
        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>Live · updates every 30s</span>
      </div>
    </div>
  );
}


function PostCard({ post, onApprove, onDecline, onDelete }) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [expanded, setExpanded] = useState(false);
  const pc = PLATFORM_COLORS[post.platform] || '#666';

  return (
    <div className="p-3 rounded-xl mb-2 llmc-card fade-in" data-testid={`post-card-${post.id}`}
      style={{ borderLeft: `3px solid ${pc}` }}>
      {/* Top row */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase"
          style={{ background: `${pc}22`, color: pc, border: `1px solid ${pc}44` }}>
          {post.platform}
        </span>
        {post.version > 1 && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(240,194,127,0.15)', color: '#f0c27f' }}>
            V{post.version}
          </span>
        )}
        {post.research_backed && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,212,184,0.1)', color: '#00d4b8' }}>
            Research
          </span>
        )}
        <span className="ml-auto text-[9px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
          {new Date(post.created_at).toLocaleDateString()}
        </span>
      </div>

      <div className="text-xs font-semibold text-white mb-1 leading-tight">{post.title || 'Untitled Draft'}</div>
      <div className="text-[11px] leading-relaxed mb-2"
        style={{
          color: 'rgba(255,255,255,0.45)',
          display: '-webkit-box',
          WebkitLineClamp: expanded ? 99 : 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
        {post.content}
      </div>

      {post.content?.length > 180 && (
        <button onClick={() => setExpanded(!expanded)} className="text-[10px] mb-2"
          style={{ color: 'rgba(124,94,250,0.7)' }}>
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}

      {/* Actions */}
      {post.status === 'pending_review' && (
        <div className="flex gap-1.5">
          <button onClick={() => onApprove(post.id)} data-testid={`approve-btn-${post.id}`}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold"
            style={{ background: 'rgba(61,255,160,0.12)', border: '1px solid rgba(61,255,160,0.3)', color: '#3dffa0' }}>
            <CheckCircle size={11} /> Approve
          </button>
          <button onClick={() => setShowFeedback(!showFeedback)} data-testid={`decline-btn-${post.id}`}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold"
            style={{ background: 'rgba(235,87,87,0.1)', border: '1px solid rgba(235,87,87,0.3)', color: '#eb5757' }}>
            <RotateCcw size={11} /> Decline
          </button>
        </div>
      )}

      {showFeedback && (
        <div className="mt-2 flex flex-col gap-1.5">
          <textarea rows={2} value={feedback} onChange={e => setFeedback(e.target.value)}
            data-testid={`feedback-input-${post.id}`}
            className="w-full text-[11px] px-2 py-1.5 rounded-lg llmc-input resize-none"
            placeholder="What needs to change? The AI will rewrite using your feedback..." />
          <div className="flex gap-1.5">
            <button onClick={() => { onDecline(post.id, feedback); setShowFeedback(false); setFeedback(''); }}
              className="flex-1 py-1.5 rounded-lg text-[11px] font-bold"
              style={{ background: '#eb5757', color: '#fff' }}>
              Submit & Rewrite
            </button>
            <button onClick={() => setShowFeedback(false)} className="px-2 py-1.5 rounded-lg text-[11px] llmc-btn-ghost">
              Cancel
            </button>
          </div>
        </div>
      )}

      {post.scheduled_for && post.status === 'scheduled' && (
        <div className="flex items-center gap-1 mt-1.5 text-[10px]" style={{ color: '#a68eff' }}>
          <Clock size={10} />{new Date(post.scheduled_for).toLocaleString()}
        </div>
      )}

      {post.feedback_history?.length > 0 && (
        <div className="flex items-center gap-1 mt-1 text-[10px]" style={{ color: 'rgba(240,194,127,0.7)' }}>
          <MessageSquare size={10} />
          {post.feedback_history.length} feedback round{post.feedback_history.length > 1 ? 's' : ''}
        </div>
      )}

      <div className="flex items-center justify-end mt-1">
        <button onClick={() => onDelete(post.id)} data-testid={`delete-btn-${post.id}`}
          className="p-1 rounded opacity-30 hover:opacity-80 transition-opacity"
          style={{ color: '#eb5757' }}>
          <Trash2 size={10} />
        </button>
      </div>
    </div>
  );
}


export default function ContentPreviewBoard() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedOpen, setFeedOpen] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/social/queue/all`);
      setPosts(data.posts || []);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (postId) => {
    await axios.post(`${API}/api/social/approve`, { post_id: postId });
    load();
  };

  const decline = async (postId, feedback) => {
    await axios.post(`${API}/api/social/decline`, { post_id: postId, feedback });
    load();
  };

  const deletePost = async (postId) => {
    try { await axios.delete(`${API}/api/social/queue/${postId}`); } catch {}
    load();
  };

  const pending = posts.filter(p => p.status === 'pending_review').length;

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
        style={{ background: 'rgba(10,10,20,0.95)', borderBottom: '1px solid var(--llmc-border)' }}>
        <Layout size={20} style={{ color: '#7c5efa', flexShrink: 0 }} />
        <div>
          <div className="font-black text-white text-sm">Content Preview Board</div>
          <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
            {posts.length} posts · {pending > 0 ? `${pending} awaiting review` : 'All clear'}
          </div>
        </div>
        {pending > 0 && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(240,194,127,0.15)', color: '#f0c27f', border: '1px solid rgba(240,194,127,0.3)' }}>
            {pending} pending
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} disabled={loading} className="p-1.5 rounded-lg hover:opacity-70 transition-opacity"
            data-testid="refresh-board-btn" style={{ opacity: 0.4, color: 'var(--llmc-text-muted)' }}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Body: Kanban + Discord Feed */}
      <div className="flex flex-1 overflow-hidden">
        {/* Kanban columns */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: '#7c5efa', borderTopColor: 'transparent' }} />
            </div>
          ) : (
            <div className="flex h-full gap-3 p-4" style={{ minWidth: `${COLUMNS.length * 275}px` }}>
              {COLUMNS.map(col => {
                const colPosts = posts.filter(p => p.status === col.id);
                return (
                  <div key={col.id} data-testid={`column-${col.id}`}
                    className="flex flex-col rounded-2xl overflow-hidden"
                    style={{ width: 268, minWidth: 268, background: col.bg, border: `1px solid ${col.color}20` }}>
                    {/* Column header */}
                    <div className="px-3 py-2.5 flex items-center gap-2 flex-shrink-0"
                      style={{ borderBottom: `1px solid ${col.color}20`, background: `${col.color}08` }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                      <span className="text-xs font-bold flex-1" style={{ color: col.color }}>{col.label}</span>
                      <span className="text-xs font-black px-2 py-0.5 rounded-full"
                        style={{ background: `${col.color}20`, color: col.color }}>
                        {colPosts.length}
                      </span>
                    </div>
                    {/* Posts list */}
                    <div className="flex-1 overflow-y-auto p-2">
                      {colPosts.length === 0 ? (
                        <div className="flex items-center justify-center h-16 rounded-xl border border-dashed"
                          style={{ borderColor: `${col.color}25` }}>
                          <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.15)' }}>Empty</span>
                        </div>
                      ) : colPosts.map(post => (
                        <PostCard key={post.id} post={post} onApprove={approve} onDecline={decline} onDelete={deletePost} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Discord feed panel */}
        <div className="flex items-stretch">
          {!feedOpen && (
            <div className="flex flex-col items-center gap-1 py-4 px-1.5 cursor-pointer transition-all hover:opacity-80"
              style={{ borderLeft: '1px solid rgba(88,101,242,0.2)', background: 'rgba(88,101,242,0.04)' }}
              onClick={() => setFeedOpen(true)} title="Open Discord Activity Feed"
              data-testid="discord-feed-toggle">
              <MessageCircle size={14} style={{ color: '#7289da' }} />
              <span className="text-[9px] font-bold uppercase tracking-widest"
                style={{ writingMode: 'vertical-rl', color: 'rgba(114,137,218,0.6)' }}>
                Discord Feed
              </span>
            </div>
          )}
          <DiscordFeedPanel open={feedOpen} onToggle={() => setFeedOpen(!feedOpen)} />
        </div>
      </div>
    </div>
  );
}
